import * as React from 'react'
import { fabric } from 'fabric'
import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { fetchS3UrlBothQuality, withS3Url } from '../file/withS3Url'
import { waitForAnimationFrame } from '../util/idle'
import { assumeExhaustion } from '../../../shared/lib/typeHelpers.no-deps'

interface Props {
    deps: CommonDeps
    photoUrl: string
    annotationUrl?: string
    onChange: (snapshot: string) => void
    onSelectionChange: (object: fabric.Object) => void
    onTextEditStart: () => void
    onTextEditEnd: () => void
    onDrawStart: () => void
    onDrawEnd: () => void
}

// This is typed as `any` in fabric. I refuse to copy that typing here.
interface FabricSVGOptions {
    width: number
}

enum EditMode {
    Draw,
    Off,
}

export enum Shape {
    Square = 'square',
    Line = 'line',
    Circle = 'circle',
    Arrow = 'arrow',
}

const isTextObject = (object: fabric.Object): object is fabric.Text => object.type === 'text'

const BASE_FONT_SIZE = 29
const VERTICAL_RATIO = 0.3131

export class AnnotationCanvas extends React.Component<Props, {}> {
    public render = () => renderCatch(this.props.deps, this.tryRender)

    private photoElement: HTMLImageElement
    private canvasElement: HTMLCanvasElement
    private canvas: fabric.Canvas
    private fontFamily: string

    private setEditMode = (mode: EditMode) => {
        this.canvas.discardActiveObject().renderAll()

        switch (mode) {
            case EditMode.Draw:
                this.canvas.isDrawingMode = true
                return
            case EditMode.Off:
                this.canvas.isDrawingMode = false
                return
            default:
                assumeExhaustion(mode)
        }
    }

    public async componentDidMount() {
        this.canvas = new fabric.Canvas(this.canvasElement, {
            isDrawingMode: false,
            selection: false,
        })

        // Make the canvas resize with the window
        window.addEventListener('resize', this.onResize)

        this.canvas.on('object:added', this.onObjectChange)
        this.canvas.on('object:modified', this.onObjectChange)
        this.canvas.on('object:removed', this.onObjectChange)
        this.canvas.on('mouse:down', this.handleMouseDown)
        this.canvas.on('mouse:up', this.handleMouseUp)

        this.canvas.on('selection:created', this.handleObjectSelection)
        this.canvas.on('selection:cleared', this.handleObjectSelectionCleared)
        // Set default brush
        this.canvas.freeDrawingBrush.color = 'white'
        this.canvas.freeDrawingBrush.width = 3
        this.fontFamily = getComputedStyle(document.body).fontFamily
    }

    private handleObjectSelection = (event: fabric.IEvent) => {
        this.props.onSelectionChange(event.target)

        // Disable mouse event handlers when an object is selected
        this.canvas.off('mouse:down', this.handleMouseDown)
    }

    private handleObjectSelectionCleared = () => {
        this.props.onSelectionChange(null)
        // This set timeout means the mouse down event doesn't fire
        // for the mouse event that caused the deselection
        setTimeout(() => {
            this.canvas.on('mouse:down', this.handleMouseDown)
        }, 0)
    }

    private onObjectChange = () => {
        this.props.onChange(JSON.stringify(this.canvas))
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this.onResize)
        this.canvas.off('object:added', this.onObjectChange)
        this.canvas.off('object:modified', this.onObjectChange)
        this.canvas.off('object:removed', this.onObjectChange)

        this.canvas.off('mouse:down', this.handleMouseDown)
        this.canvas.off('mouse:up', this.handleMouseUp)

        this.canvas.off('selection:created', this.handleObjectSelection)
        this.canvas.off('selection:cleared', this.handleObjectSelectionCleared)
    }

    private handleMouseDown = () => {
        if (this.canvas.isDrawingMode) {
            this.props.onDrawStart()
        }
    }

    private handleMouseUp = () => {
        if (this.canvas.isDrawingMode) {
            this.props.onDrawEnd()
        }
    }

    private async getFabricObjects() {
        if (this.props.annotationUrl == null) {
            return Promise.resolve([null, null])
        }

        let url = this.props.annotationUrl
        const isBlob = url.startsWith('blob')
        // http(s)://s3.[region].amazonaws.com/[the rest of the path]
        const isS3Url = url.match(/^https?:\/\/s3\.[^./]+\.amazonaws.com\/.*/)
        if (!(isBlob || isS3Url)) {
            ;({ fullSizeS3Url: url } = await fetchS3UrlBothQuality(this.props.deps, url))
        }
        if (url == null) {
            this.props.deps.reportError('Cannot resolve annotation from URL', { url: this.props.annotationUrl })
            return Promise.resolve([null, null])
        }

        return new Promise<[fabric.Object[], FabricSVGOptions]>((resolve) =>
            fabric.loadSVGFromURL(url, (objects, options) => {
                objects.forEach((object) => {
                    if (isTextObject(object) === false) {
                        return
                    }

                    // There are small differences between the way SVG renders
                    // text and Fabric renders text. This is a known issue.
                    // https://github.com/fabricjs/fabric.js/issues/974
                    // These adjustments should account for those differences
                    // in our use case.
                    object.left -= object.width / 2
                    object.top += object.fontSize * VERTICAL_RATIO
                })

                resolve([objects, options])
            })
        )
    }

    private onImgLoad = async () => {
        // Wait for one animation frame for the image to be start to be drawn
        // And wait for the second animation frame for the image to be finished drawing
        // https://stackoverflow.com/a/24674486/2648987
        await waitForAnimationFrame()
        await waitForAnimationFrame()

        const [width, height] = this.photoEffectiveSize()
        this.canvas.setDimensions({ width, height })

        // Load previous annotation
        const [objects, options] = await this.getFabricObjects()
        if (objects != null) {
            const group = fabric.util.groupSVGElements(objects, options)
            // The annotation may not be the same size as the underlying image. Since we can scale the canvas to
            // whatever size we want, we can set the annotation to be the full size of the canvas and set the canvas to
            // the same size as the image so we don't have to worry about converting later.
            group.scale(width / options.width)
            // It's confusing to select the whole group
            // We might be able to make individual items selecable in future
            group.selectable = false
            this.canvas.add(group).renderAll()
        }
    }

    // https://stackoverflow.com/a/60614910/2648987
    private onResize = () => {
        if (this.canvas == null || this.photoElement == null) return

        const [photoElementWidth] = this.photoEffectiveSize()

        const ratio = this.canvas.getWidth() / this.canvas.getHeight()

        const scale = photoElementWidth / this.canvas.getWidth()
        const zoom = this.canvas.getZoom() * scale

        this.canvas.setDimensions({ width: photoElementWidth, height: photoElementWidth / ratio })
        this.canvas.setViewportTransform([zoom, 0, 0, zoom, 0, 0])
    }

    public clear = () => {
        this.canvas.clear()
    }

    public setColor = (color: string) => {
        this.canvas.freeDrawingBrush.color = color
        const selectedItem = this.canvas.getActiveObject()
        if (selectedItem) {
            const objects = selectedItem.type === 'group' ? (selectedItem as fabric.Group).getObjects() : [selectedItem]

            objects.forEach((object) => {
                if (object.fill != null && object.fill !== 'rgba(0,0,0,0)') {
                    object.set({
                        fill: color,
                        stroke: color,
                    })
                } else {
                    object.set({
                        stroke: color,
                    })
                }
            })

            // Changing colour doesn't trigger the change handler to fire
            // So we do it manually here.
            this.onObjectChange()
            this.canvas.renderAll()
        }
    }

    public deleteActiveObject = () => {
        const activeObject = this.canvas.getActiveObject()
        if (activeObject == null) throw new Error('No selected object')
        this.canvas.remove(activeObject)
    }

    public enableDrawingMode = () => {
        this.setEditMode(EditMode.Draw)
    }

    public disableDrawingMode = () => {
        this.setEditMode(EditMode.Off)
    }

    public disbleTextMode = () => {
        this.setEditMode(EditMode.Off)
    }

    public disableEditMode = () => {
        this.setEditMode(EditMode.Off)
    }

    public addShape = (shape: Shape) => {
        let object: fabric.Object
        const centerX = this.canvas.width / 2
        const centerY = this.canvas.height / 2
        switch (shape) {
            case Shape.Square:
                const rect = new fabric.Rect({
                    left: centerX - 40,
                    top: centerY - 40,
                    originX: 'left',
                    originY: 'top',
                    width: 80,
                    height: 80,
                    rx: 10,
                    ry: 10,
                    fill: 'rgba(0,0,0,0)',
                    stroke: this.canvas.freeDrawingBrush.color,
                    strokeWidth: 10,
                    strokeUniform: true,
                })
                object = rect
                break
            case Shape.Line:
                const line = new fabric.Line([centerX - 40, centerY, centerX + 40, centerY], {
                    strokeWidth: 5,
                    fill: this.canvas.freeDrawingBrush.color,
                    stroke: this.canvas.freeDrawingBrush.color,
                    originX: 'center',
                    originY: 'center',
                    strokeUniform: true,
                })
                object = line
                break
            case Shape.Circle:
                const circle = new fabric.Ellipse({
                    left: centerX - 40,
                    top: centerY - 40,
                    rx: 80,
                    ry: 80,
                    fill: 'rgba(0,0,0,0)',
                    stroke: this.canvas.freeDrawingBrush.color,
                    strokeWidth: 10,
                    strokeUniform: true,
                })
                object = circle
                break
            case Shape.Arrow:
                const arrowLine = new fabric.Line([centerX - 40, centerY, centerX + 40, centerY], {
                    strokeWidth: 5,
                    fill: this.canvas.freeDrawingBrush.color,
                    stroke: this.canvas.freeDrawingBrush.color,
                    originX: 'center',
                    originY: 'center',
                })

                const triangle = new fabric.Triangle({
                    width: 30,
                    height: 25,
                    fill: this.canvas.freeDrawingBrush.color,
                    left: centerX + 40,
                    top: centerY,
                    angle: 90,
                    originX: 'center',
                    originY: 'center',
                })
                object = new fabric.Group([arrowLine, triangle])
                break
            default:
                assumeExhaustion(shape)
        }

        this.canvas.add(object).renderAll()
        this.canvas.setActiveObject(object).renderAll()
    }

    public addTextElement = () => {
        const text = new fabric.IText('', {
            left: this.canvas.width / 2,
            top: this.canvas.height / 2,
            fontFamily: this.fontFamily,
            fontWeight: 700,
            fill: this.canvas.freeDrawingBrush.color,
            fontSize: BASE_FONT_SIZE,
        })

        this.canvas.add(text)
        this.canvas.setActiveObject(text)
        this.canvas.renderAll()
        text.on('editing:entered', () => {
            this.props.onTextEditStart()
        })
        text.on('editing:exited', () => {
            this.props.onTextEditEnd()
        })
        text.enterEditing()

        text.hiddenTextarea.focus()
    }

    public endTextEdit = () => {
        const activeObject = this.canvas.getActiveObject()
        if (activeObject == null) throw new Error('No selected object')
        if (activeObject.type !== 'i-text') {
            throw new Error('Selected Item is not a text object')
        }
        ;(activeObject as fabric.IText).exitEditing()
        this.canvas.setActiveObject(activeObject).renderAll()
    }

    public loadFromJSON = (state: string) => {
        // Remove the listeners because otherwise they fire when the canvas is reloaded
        this.canvas.off('object:added', this.onObjectChange)
        this.canvas.off('object:modified', this.onObjectChange)
        this.canvas.off('object:removed', this.onObjectChange)

        this.canvas.loadFromJSON(state, () => {
            this.canvas.on('object:added', this.onObjectChange)
            this.canvas.on('object:modified', this.onObjectChange)
            this.canvas.on('object:removed', this.onObjectChange)
        })
    }

    public get snapshot() {
        return JSON.stringify(this.canvas)
    }

    public toBlob = () => {
        const svg = this.canvas.toSVG()
        return new Blob([svg], { type: 'image/svg+xml' })
    }

    public discardSelection = () => {
        this.canvas.discardActiveObject().renderAll()
    }

    private photoEffectiveSize() {
        // https://stackoverflow.com/a/52187440/2648987

        const ratio = this.photoElement.naturalWidth / this.photoElement.naturalHeight
        let width = this.photoElement.height * ratio
        let height = this.photoElement.height

        if (width > this.photoElement.width) {
            width = this.photoElement.width
            height = this.photoElement.width / ratio
        }

        return [width, height]
    }

    public tryRender = () => {
        return (
            <div className="AppCore__photo__AnnotationCanvas">
                {withS3Url(this.props.deps, this.props.photoUrl, ({ fullSizeS3Url }) => (
                    <img
                        className="AppCore__photo__AnnotationCanvas__photo"
                        ref={(ref) => (this.photoElement = ref)}
                        src={fullSizeS3Url ?? this.props.photoUrl}
                        data-testid="annotation-canvas-image"
                        onLoad={this.onImgLoad}
                    />
                ))}
                <MutableCanvas setRef={(ref: any) => (this.canvasElement = ref)} />
            </div>
        )
    }
}

// If the canvas is placed onto the parent element then when fabric mutates it,
// react will crash due to the dom changing under it
function MutableCanvas({ setRef }: { setRef: (ref: HTMLElement) => void }) {
    return (
        <div style={{ display: 'contents' }}>
            <canvas ref={setRef}></canvas>
        </div>
    )
}
